import { useEffect, useRef, useState } from 'react';
import { MapPin, Search, X } from 'lucide-react';
import { DescriptionText, inputStyles } from '@workspace/silk-ui';
import { cn } from '@/lib/utils';
import { loadGoogleMapsPlacesApi } from '@/lib/googleMapsPlacesLoader';

export type EligibilityAddressSelection = {
  address: string;
  googlePlaceId?: string;
  lat?: number;
  lng?: number;
  placeData?: Record<string, unknown>;
};

function serializePlace(place: Record<string, unknown>, lat?: number, lng?: number): Record<string, unknown> {
  return {
    formatted_address: typeof place.formatted_address === 'string' ? place.formatted_address : null,
    name: typeof place.name === 'string' ? place.name : null,
    place_id: typeof place.place_id === 'string' ? place.place_id : null,
    types: Array.isArray(place.types) ? place.types : [],
    address_components: Array.isArray(place.address_components) ? place.address_components : [],
    geometry: lat !== undefined && lng !== undefined ? { location: { lat, lng } } : null,
  };
}

function splitAddress(value: string): { street: string; rest: string } {
  const commaIdx = value.indexOf(',');
  if (commaIdx === -1) {
    return { street: value, rest: '' };
  }
  return {
    street: value.slice(0, commaIdx).trim(),
    rest: value.slice(commaIdx + 1).trim(),
  };
}

type CommerceEligibilityAddressInputProps = {
  value: string;
  googlePlaceId?: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  onSelect: (selection: EligibilityAddressSelection) => void;
  onClearSelection: () => void;
};

export function CommerceEligibilityAddressInput({
  value,
  googlePlaceId,
  disabled = false,
  onValueChange,
  onSelect,
  onClearSelection,
}: CommerceEligibilityAddressInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suppressNextInputChangeRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const autocompleteRef = useRef<{
    addListener: (eventName: string, callback: () => void) => { remove?: () => void } | void;
    getPlace: () => Record<string, unknown> | null | undefined;
  } | null>(null);
  const [autocompleteError, setAutocompleteError] = useState<string | null>(null);
  const hasGoogleSelection = Boolean(googlePlaceId);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (hasGoogleSelection) {
      return;
    }

    let cancelled = false;
    let listener: { remove?: () => void } | void;

    const setupAutocomplete = async () => {
      if (autocompleteRef.current || !inputRef.current) {
        return;
      }

      try {
        await loadGoogleMapsPlacesApi();
      } catch {
        if (!cancelled) {
          setAutocompleteError('Google autocomplete unavailable. You can still type an address manually.');
        }
        return;
      }

      const Autocomplete = window.google?.maps?.places?.Autocomplete;
      if (!Autocomplete || cancelled || !inputRef.current) {
        if (!cancelled && !Autocomplete) {
          setAutocompleteError('Google autocomplete unavailable. You can still type an address manually.');
        }
        return;
      }

      setAutocompleteError(null);
      const autocomplete = new Autocomplete(inputRef.current, {
        fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types', 'address_components'],
        types: ['address'],
      });
      autocompleteRef.current = autocomplete;
      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place) {
          return;
        }

        const geometry = place.geometry as
          | { location?: { lat?: () => number; lng?: () => number } }
          | undefined;
        const lat = typeof geometry?.location?.lat === 'function' ? geometry.location.lat() : undefined;
        const lng = typeof geometry?.location?.lng === 'function' ? geometry.location.lng() : undefined;
        const address =
          (typeof place.formatted_address === 'string' && place.formatted_address.trim()) ||
          (typeof place.name === 'string' && place.name.trim()) ||
          inputRef.current?.value ||
          '';

        // Google may dispatch a follow-on input change after place selection.
        // Ignore that single event so the parent does not immediately clear the selected place metadata.
        suppressNextInputChangeRef.current = true;
        onSelectRef.current({
          address,
          googlePlaceId: typeof place.place_id === 'string' ? place.place_id : undefined,
          lat,
          lng,
          placeData: serializePlace(place, lat, lng),
        });
      });
    };

    void setupAutocomplete();

    return () => {
      cancelled = true;
      if (typeof listener === 'object' && listener && typeof listener.remove === 'function') {
        listener.remove();
      }
      autocompleteRef.current = null;
    };
  }, [hasGoogleSelection]);

  if (hasGoogleSelection) {
    const { street, rest } = splitAddress(value);
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 rounded-md border border-border/60 bg-muted/10 px-4 py-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
            <MapPin className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold tracking-tight text-foreground">{street}</div>
            {rest ? <div className="truncate text-xs font-medium text-muted-foreground">{rest}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClearSelection}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-bold text-muted-foreground transition hover:bg-muted/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-3 w-3" />
            Change
          </button>
        </div>
        <DescriptionText className="text-[11px] text-muted-foreground">
          Selected from Google Places. Choose Change to switch back to a typed address.
        </DescriptionText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary/60">
          <Search className="h-4 w-4" />
        </div>
        <input
          ref={inputRef}
          value={value}
          disabled={disabled}
          onChange={event => {
            if (suppressNextInputChangeRef.current) {
              suppressNextInputChangeRef.current = false;
              return;
            }
            onValueChange(event.target.value);
          }}
          placeholder="123 Main St, City, State"
          autoComplete="street-address"
          className={cn(inputStyles, 'pl-10')}
        />
      </div>
      {autocompleteError ? (
        <DescriptionText className="text-[11px] text-muted-foreground">{autocompleteError}</DescriptionText>
      ) : (
        <DescriptionText className="text-[11px] text-muted-foreground">
          Search with Google Places or keep typing to use a plain address string.
        </DescriptionText>
      )}
    </div>
  );
}
